import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import LineBarsIcon from 'LuxKit/Icons/line/LineBarsIcon'
import LineEllipsisVIcon from 'LuxKit/Icons/line/LineEllipsisVIcon'
import LineShoppingCartIcon from 'LuxKit/Icons/line/LineShoppingCartIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'

import { useEventSubscription, useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import type { ChatUiActionData } from 'tripPlanner/api/types'
import { CHAT_TRIP_PLANNER_NEW_PATH } from 'tripPlanner/config'
import CartButton from '../components/CartButton'
import { useChatUiAction } from '../contexts/ChatUiActionContext'
import { normalizeRightPanelView } from '../hooks/useAICameraState'
import TripChatMobileTabs, { type TripChatMobileTabId, type TripChatMobileTabItem } from './TripChatMobileTabs'
import Clickable from 'components/Common/Clickable'
import { TripPlannerStyledLogo } from 'tripPlanner/components/Header/Shared'
import PrivateChatBanner from 'tripPlanner/components/PrivateChatBanner'
import CustomerVisibleChatBanner from 'tripPlanner/components/CustomerVisibleChatBanner'
import { selectSpoofBanner } from 'tripPlanner/utils/selectSpoofBanner'
import noop from 'lib/function/noop'

function isTimelineRightPanelUiAction(data: ChatUiActionData): boolean {
  return data.action === 'set_right_panel_view' && normalizeRightPanelView(data.view) === 'timeline'
}

interface TripChatMobileAppShellProps {
  children?: ReactNode
  onMenuOpen?: () => void
  onCurrentTripActionsOpen?: () => void
  tabPanels?: Partial<Record<TripChatMobileTabId, ReactNode>>
  showTabs?: boolean
  showComposerReservedSpace?: boolean
  showCartButton?: boolean
  isCartButtonDisabled?: boolean
  cartItemCount?: number
  cartTotalPrice?: number
  isCartButtonLoading?: boolean
  isSpoofed?: boolean
  hiddenFromCustomer?: boolean
  chatExists?: boolean
  /** Used only to reset banner dismissal when switching to a different chat. */
  chatId?: number
  onCreateNewChat?: () => void
}

interface TripChatMobileAppShellContextValue {
  activeTab: TripChatMobileTabId
  setActiveTab: (tabId: TripChatMobileTabId) => void
}

const TripChatMobileAppShellContext = createContext<TripChatMobileAppShellContextValue | undefined>(undefined)

const ShellRoot = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-top: env(safe-area-inset-top);
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
`

const ShellHeader = styled.header`
  display: grid;
  grid-template-columns: ${rem(44)} minmax(0, 1fr) auto;
  align-items: center;
  flex-shrink: 0;
  min-height: ${rem(60)};
  padding-block: 0;
  padding-inline: ${rem(4)} ${rem(8)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const HeaderSpacer = styled.span`
  width: ${rem(44)};
  height: ${rem(44)};
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(4)};
  justify-self: end;
  min-width: 0;
`

const HeaderBrandGroup = styled.div`
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: ${rem(12)};
  justify-self: start;
`

const HeaderHomeLogoLink = styled(Clickable)`
  display: inline-flex;
  flex-shrink: 0;
  color: ${props => props.theme.palette.neutral.surface.normalContrast};

  &:not(:disabled) {
    &:hover {
      background-color: transparent;
      border-color: transparent;
      color: ${props => props.theme.palette.neutral.surface.normalContrast};
    }
  }
`

const HeaderBrandLink = styled(Clickable)`
  display: inline-flex;
  min-width: 0;
  align-items: center;
  color: ${props => props.theme.palette.neutral.surface.normalContrast};

  &:not(:disabled) {
    &:hover {
      background-color: transparent;
      border-color: transparent;
      color: ${props => props.theme.palette.neutral.surface.normalContrast};
    }
  }
`

const HeaderBrandText = styled(BodyText)`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const IconButton = styled.button`
  display: inline-flex;
  width: ${rem(44)};
  height: ${rem(44)};
  align-items: center;
  justify-content: center;
  justify-self: center;
  border: 0;
  border-radius: ${props => props.theme.borderRadius.S};
  background: transparent;
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  cursor: pointer;

  &:disabled {
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
    cursor: default;
  }

  &:focus-visible {
    outline: ${rem(2)} solid ${props => props.theme.palette.highlight.primary.normalForeground};
    outline-offset: ${rem(2)};
  }
`

const ShellBody = styled.main`
  position: relative;
  display: flex;
  min-height: 0;
  flex: 1 1 auto;
  flex-direction: column;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const TabPanels = styled.div`
  position: relative;
  min-height: 0;
  flex: 1 1 auto;
  overflow: hidden;
`

const TabPanel = styled.div`
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: ${rem(20)};

  &.custom {
    overflow: hidden;
    padding: 0;
  }

  &[hidden] {
    display: none;
  }
`

const PlaceholderView = styled.div`
  display: flex;
  min-height: 100%;
  flex-direction: column;
  justify-content: center;
  gap: ${rem(8)};
  text-align: center;
`

const ComposerReservedSpace = styled.footer`
  min-height: calc(${rem(68)} + env(safe-area-inset-bottom));
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const DEFAULT_TAB_PANELS: Partial<Record<TripChatMobileTabId, ReactNode>> = {}

interface TripChatMobileTabPanelProps {
  activeTab: TripChatMobileTabId
  labels: ReturnType<typeof useTripChatMobileLabels>
  tab: TripChatMobileTabItem
  tabPanels: Partial<Record<TripChatMobileTabId, ReactNode>>
}

function TripChatMobileTabPanel({ activeTab, labels, tab, tabPanels }: TripChatMobileTabPanelProps) {
  const panelContent = tabPanels[tab.id]
  const hasPanelContent = panelContent !== undefined

  return (
    <TabPanel
      className={hasPanelContent ? 'custom' : undefined}
      data-testid={`trip-chat-mobile-tab-panel-${tab.id}`}
      hidden={activeTab !== tab.id}
    >
      {hasPanelContent && panelContent}
      {!hasPanelContent && (
        <PlaceholderView>
          <Heading variant="heading5" as="h2">
            {tab.label}
          </Heading>
          <BodyText variant="medium">
            {labels.placeholders[tab.id]}
          </BodyText>
        </PlaceholderView>
      )}
    </TabPanel>
  )
}

function useTripChatMobileLabels() {
  const intl = useIntl()

  return useMemo(() => ({
    menuButton: intl.formatMessage({
      id: 'Rh5EB1l_8dTFdLNrgEnU',
      defaultMessage: 'Open trip menu',
      description: 'Accessibility label for the mobile trip planner menu button in the app shell header',
    }),
    homeLogo: intl.formatMessage({
      id: 'X2WEFDaGFMpKE1h4Uxob',
      defaultMessage: 'Go to Luxury Escapes homepage',
      description: 'Accessibility label for the Luxury Escapes logo link in the mobile trip planner header',
    }),
    aiTripPlanner: intl.formatMessage({
      id: 'EYQsWpC_SwW6ZS0Xb0Yz',
      defaultMessage: 'AI TripPlanner',
      description: 'Branding label for the AI TripPlanner feature in the header',
    }),
    cartButton: intl.formatMessage({
      id: 'gUgzcjMkoSgOtM_qF0I3',
      defaultMessage: 'Open trip cart',
      description: 'Accessibility label for the mobile trip cart button in the app shell header',
    }),
    currentTripActionsButton: intl.formatMessage({
      id: 'ghqTChXm5vuqvEyzR_pE',
      defaultMessage: 'Open current trip actions',
      description: 'Accessibility label for the mobile TripPlanner current itinerary actions menu button',
    }),
    tabList: intl.formatMessage({
      id: 'HwL8PoaVwNML5Nh72udF',
      defaultMessage: 'Trip planner mobile views',
      description: 'Accessibility label for the mobile trip planner tab navigation control',
    }),
    tabs: {
      chat: intl.formatMessage({
        id: 'W6AYq4RaOfBu_SyMN7Vv',
        defaultMessage: 'Chat',
        description: 'Tab label for the mobile AI TripPlanner chat conversation view',
      }),
      timeline: intl.formatMessage({
        id: 'Ubu2tgXvGlszkJ3USdI-',
        defaultMessage: 'Timeline',
        description: 'Tab label for the mobile AI TripPlanner timeline view',
      }),
      list: intl.formatMessage({
        id: 'v3ioTc-88OqTbL9bnkQA',
        defaultMessage: 'List',
        description: 'Tab label for the mobile AI TripPlanner itinerary list view',
      }),
      map: intl.formatMessage({
        id: 'IASdasfMP-rhaQoHcb5w',
        defaultMessage: 'Map',
        description: 'Tab label for the mobile AI TripPlanner trip map view',
      }),
    },
    placeholders: {
      chat: intl.formatMessage({
        id: 'EwQ3FblF7olWsyrdOhnq',
        defaultMessage: 'Your planning chat will appear here.',
        description: 'Passive placeholder text shown before the mobile chat view is wired',
      }),
      timeline: intl.formatMessage({
        id: 'KGa4wVpkemPhqPRpVEcK',
        defaultMessage: 'Your trip timeline will appear here.',
        description: 'Passive placeholder text shown before the mobile timeline view is wired',
      }),
      list: intl.formatMessage({
        id: '1mQR6iQWYoQO8jSRvX6z',
        defaultMessage: 'Your trip list will appear here.',
        description: 'Passive placeholder text shown before the mobile list view is wired',
      }),
      map: intl.formatMessage({
        id: '_zTqwDdDiH4vhnUY86fd',
        defaultMessage: 'Your trip map will appear here.',
        description: 'Passive placeholder text shown before the mobile map view is wired',
      }),
    },
  }), [intl])
}

function TripChatMobileAppShell({
  children,
  onMenuOpen,
  onCurrentTripActionsOpen,
  tabPanels = DEFAULT_TAB_PANELS,
  showTabs = true,
  showComposerReservedSpace = true,
  showCartButton = false,
  isCartButtonDisabled = false,
  cartItemCount = 0,
  cartTotalPrice = 0,
  isCartButtonLoading = false,
  isSpoofed = false,
  hiddenFromCustomer = true,
  chatExists = false,
  chatId,
  onCreateNewChat = noop,
}: TripChatMobileAppShellProps) {
  const labels = useTripChatMobileLabels()
  const eventBus = useTripPlannerEventBus()
  const { pendingUiAction, clearPendingUiAction } = useChatUiAction()
  const [activeTab, setActiveTab] = useState<TripChatMobileTabId>('chat')
  const [isPrivateChatBannerDismissed, setIsPrivateChatBannerDismissed] = useState(false)
  const [isCustomerVisibleChatBannerDismissed, setIsCustomerVisibleChatBannerDismissed] = useState(false)
  const spoofBanner = selectSpoofBanner({ isSpoofed, hiddenFromCustomer, chatExists })

  // A dismissal on one chat shouldn't hide the banner on a different chat.
  useEffect(() => {
    setIsPrivateChatBannerDismissed(false)
    setIsCustomerVisibleChatBannerDismissed(false)
  }, [chatId])

  const applyTimelineUiAction = useCallback((data: ChatUiActionData) => {
    if (!isTimelineRightPanelUiAction(data)) return false

    // Persist even while landing hides tabs (showTabs=false); when tabs appear we
    // surface the already-selected timeline instead of silently dropping the action.
    setActiveTab('timeline')
    clearPendingUiAction()
    return true
  }, [clearPendingUiAction])

  // MapContainer (and TripChatMobileMapView) are not mounted on the landing screen,
  // so itinerary ui_action must be consumed here — the shell always mounts.
  useEventSubscription('ui_action', (event) => {
    applyTimelineUiAction(event.data)
  })

  // Non-chat surfaces (e.g. the timeline flight drawer) dispatch this when they send a
  // message to the assistant; the tabbed layout would otherwise hide the reply.
  useEventSubscription('focus_chat', () => {
    setActiveTab('chat')
  })

  useEffect(() => {
    if (!pendingUiAction) return
    applyTimelineUiAction(pendingUiAction)
  }, [applyTimelineUiAction, pendingUiAction])

  const tabs = useMemo<ReadonlyArray<TripChatMobileTabItem>>(() => [
    { id: 'chat', label: labels.tabs.chat },
    { id: 'timeline', label: labels.tabs.timeline },
    { id: 'list', label: labels.tabs.list },
    { id: 'map', label: labels.tabs.map },
  ], [labels.tabs])

  const contextValue = useMemo<TripChatMobileAppShellContextValue>(() => ({
    activeTab: showTabs ? activeTab : 'chat',
    setActiveTab,
  }), [activeTab, showTabs])

  const handleCartClick = useCallback(() => {
    eventBus.dispatch({ type: 'open_cart_panel' })
  }, [eventBus])

  const visibleTabs = useMemo(() => {
    if (showTabs) return tabs

    return tabs.filter(tab => tab.id === 'chat')
  }, [showTabs, tabs])

  const handleTabChange = useCallback((tabId: TripChatMobileTabId) => {
    if (tabId !== activeTab && activeTab === 'chat' && document.activeElement instanceof HTMLTextAreaElement) {
      document.activeElement.blur()
    }

    setActiveTab(tabId)
  }, [activeTab])

  const tabPanelElements = useMemo(() => visibleTabs.map(tab => (
    <TripChatMobileTabPanel
      key={tab.id}
      activeTab={showTabs ? activeTab : 'chat'}
      labels={labels}
      tab={tab}
      tabPanels={tabPanels}
    />
  )), [activeTab, labels, showTabs, tabPanels, visibleTabs])

  return (
    <TripChatMobileAppShellContext.Provider value={contextValue}>
      <ShellRoot data-testid="trip-chat-mobile-app-shell">
        {spoofBanner === 'private' && !isPrivateChatBannerDismissed && (
          <PrivateChatBanner onDismiss={() => setIsPrivateChatBannerDismissed(true)} />
        )}
        {spoofBanner === 'customerVisible' && !isCustomerVisibleChatBannerDismissed && (
          <CustomerVisibleChatBanner
            onCreateNewChat={onCreateNewChat}
            onDismiss={() => setIsCustomerVisibleChatBannerDismissed(true)}
          />
        )}
        <ShellHeader>
          {onMenuOpen && (
            <IconButton
              type="button"
              aria-label={labels.menuButton}
              onClick={onMenuOpen}
            >
              <LineBarsIcon size="S" />
            </IconButton>
          )}
          {!onMenuOpen && <HeaderSpacer aria-hidden="true" />}
          <HeaderBrandGroup>
            <HeaderHomeLogoLink to="/" aria-label={labels.homeLogo}>
              <TripPlannerStyledLogo />
            </HeaderHomeLogoLink>
            <HeaderBrandLink to={CHAT_TRIP_PLANNER_NEW_PATH}>
              <HeaderBrandText variant="large" weight="bold">
                {labels.aiTripPlanner}
              </HeaderBrandText>
            </HeaderBrandLink>
          </HeaderBrandGroup>
          <HeaderActions>
            {showCartButton && !isCartButtonDisabled && (
              <CartButton
                itemCount={cartItemCount}
                totalPrice={cartTotalPrice}
                isLoading={isCartButtonLoading}
                onClick={handleCartClick}
              />
            )}
            {showCartButton && isCartButtonDisabled && (
              <IconButton
                type="button"
                aria-label={labels.cartButton}
                disabled={isCartButtonDisabled}
                onClick={handleCartClick}
              >
                <LineShoppingCartIcon size="S" />
              </IconButton>
            )}
            {onCurrentTripActionsOpen && (
              <IconButton
                type="button"
                aria-label={labels.currentTripActionsButton}
                onClick={onCurrentTripActionsOpen}
              >
                <LineEllipsisVIcon size="S" />
              </IconButton>
            )}
            {!showCartButton && !onCurrentTripActionsOpen && (
              <HeaderSpacer aria-hidden="true" />
            )}
          </HeaderActions>
        </ShellHeader>
        {showTabs && (
          <TripChatMobileTabs
            activeTab={activeTab}
            ariaLabel={labels.tabList}
            tabs={tabs}
            onTabChange={handleTabChange}
          />
        )}
        <ShellBody>
          <TabPanels>
            {tabPanelElements}
          </TabPanels>
          {showComposerReservedSpace && (
            <ComposerReservedSpace
              aria-hidden="true"
              data-testid="trip-chat-mobile-composer-reserved-space"
            />
          )}
        </ShellBody>
        {children}
      </ShellRoot>
    </TripChatMobileAppShellContext.Provider>
  )
}

export function useTripChatMobileAppShell() {
  const context = useContext(TripChatMobileAppShellContext)

  if (!context) {
    throw new Error('useTripChatMobileAppShell must be used within TripChatMobileAppShell')
  }

  return context
}

export default TripChatMobileAppShell
